import { createContext, useContext, useId, useState } from 'react'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { PencilIcon } from '#/shared/ui/icons'

/**
 * The product's record card, vendored from
 * `thecargo-frontend/src/shared/ui/editable-info-card`.
 *
 * A grey title strip, a pencil that turns the body into a form, and rows on
 * dividing rules with the label in a fixed left column. Every record surface in
 * the product is built from it — company info, the admin dialogs, the shipment
 * sidebar — so a console that shows the same kind of thing should not invent a
 * second way to show it.
 *
 * The only change from the original is mechanical: `react-i18next` lookups
 * become literals, because this console ships in one language.
 */

interface EditableInfoCardGroupValue {
  /** id of the card currently being edited, or null when all are in view mode. */
  activeId: string | null
  setActive: (id: string, isActive: boolean) => void
}

const EditableInfoCardGroupContext = createContext<EditableInfoCardGroupValue | null>(null)

/**
 * Coordinates a set of sibling cards: while one is being edited the others
 * receive `dimmed` and recede. Cards used outside a group behave independently.
 */
export function EditableInfoCardGroup({ children }: { children: React.ReactNode }) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const setActive = (id: string, isActive: boolean) =>
    setActiveId((current) => {
      if (isActive) return id
      // Only the card that owns the active edit may clear it.
      return current === id ? null : current
    })
  return (
    <EditableInfoCardGroupContext.Provider value={{ activeId, setActive }}>
      {children}
    </EditableInfoCardGroupContext.Provider>
  )
}

function CardEditTrigger({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Edit"
      onClick={onClick}
      className="flex size-8 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:text-gray-900"
    >
      <PencilIcon className="size-3.5" />
    </button>
  )
}

function CardEditActions({
  onCancel,
  onSave,
  disabled,
}: {
  onCancel: () => void
  onSave: () => void
  disabled: boolean
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        className="px-3.5"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        type="button"
        size="xs"
        pressEffect
        className="px-3.5"
        disabled={disabled}
        onClick={onSave}
      >
        Save
      </Button>
    </div>
  )
}

/**
 * One label/value line. `min-h-12` keeps an empty row the height of a filled one.
 *
 * Top-aligned rather than centred, with the padding that makes a one-line row
 * come out at exactly `min-h-12` either way — so ordinary rows look unchanged
 * and a forty-line SMS body no longer leaves its label floating halfway down
 * the row, level with nothing.
 */
export function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-12 items-start gap-4 border-b border-gray-200 px-3.5 py-3.5 last:border-b-0">
      <span className="w-30 shrink-0 text-xs text-gray-500 uppercase select-none">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

/** A row's value in view mode — grey and italic-free when there is nothing there. */
export function InfoValue({ children }: { children: React.ReactNode }) {
  if (children === null || children === undefined || children === '') {
    return <span className="text-sm text-gray-300">—</span>
  }
  return <span className="text-sm font-medium text-gray-900">{children}</span>
}

interface EditableInfoCardProps {
  title: string
  children: (isEditing: boolean) => React.ReactNode
  /** Resolves true to close the card, false to keep it open with the values typed. */
  onSave: () => Promise<boolean>
  onEditStart?: () => void
  onEditCancel?: () => void
  canSave?: boolean
  isSaving?: boolean
  editable?: boolean
  /** Visually recede this card (e.g. while a sibling is being edited). */
  dimmed?: boolean
  /**
   * Drop the card chrome — no border, no grey title strip — and render the
   * title as a small section label instead.
   *
   * The detail panel's shape: there a record is read, so the rules and the
   * filled header of a form card are noise around the facts. Editing still
   * works exactly the same, which is why this is a skin and not a second
   * component.
   */
  flat?: boolean
  className?: string
}

/** Editing-state machine plus group coordination (sibling dimming). */
function useEditableCardState({
  onSave,
  onEditStart,
  onEditCancel,
  dimmed,
}: Pick<EditableInfoCardProps, 'onSave' | 'onEditStart' | 'onEditCancel'> & { dimmed: boolean }) {
  const [isEditing, setIsEditing] = useState(false)
  const group = useContext(EditableInfoCardGroupContext)
  const cardId = useId()
  // Inside a group, recede whenever a *sibling* card holds the active edit.
  const groupDimmed = group !== null && group.activeId !== null && group.activeId !== cardId

  const setEditing = (next: boolean) => {
    setIsEditing(next)
    group?.setActive(cardId, next)
  }

  return {
    isEditing,
    effectiveDimmed: dimmed || groupDimmed,
    startEdit: () => {
      onEditStart?.()
      setEditing(true)
    },
    cancelEdit: () => {
      onEditCancel?.()
      setEditing(false)
    },
    handleSave: async () => {
      if (await onSave()) setEditing(false)
    },
  }
}

export function EditableInfoCard({
  title,
  children,
  onSave,
  onEditStart,
  onEditCancel,
  canSave = true,
  isSaving = false,
  editable = true,
  dimmed = false,
  flat = false,
  className,
}: EditableInfoCardProps) {
  const { isEditing, effectiveDimmed, startEdit, cancelEdit, handleSave } = useEditableCardState({
    onSave,
    ...(onEditStart ? { onEditStart } : {}),
    ...(onEditCancel ? { onEditCancel } : {}),
    dimmed,
  })
  const mode = isEditing ? 'edit' : 'view'

  return (
    <div
      className={cn(
        'transition-opacity duration-200',
        flat ? 'flex flex-col gap-2' : 'rounded-md border border-gray-200 bg-white',
        effectiveDimmed && 'pointer-events-none opacity-45',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-2.5',
          flat
            ? 'min-h-6'
            : 'h-12 rounded-t-md border-b border-gray-200 bg-gray-100 px-2.5',
        )}
      >
        <span
          className={cn(
            'select-none',
            flat
              ? 'text-xs leading-none text-gray-500 uppercase'
              : 'text-sm font-medium text-gray-900',
          )}
        >
          {title}
        </span>
        {editable ? (
          <div key={mode} className="animate-in fade-in duration-150">
            {isEditing ? (
              <CardEditActions
                onCancel={cancelEdit}
                onSave={() => void handleSave()}
                disabled={isSaving || !canSave}
              />
            ) : (
              <CardEditTrigger onClick={startEdit} />
            )}
          </div>
        ) : null}
      </div>
      <div key={mode} className="animate-in fade-in flex flex-col duration-200">
        {children(isEditing)}
      </div>
    </div>
  )
}
